import { makeAutoObservable } from 'mobx';
import type { BibleLocation, Note } from '@skjv/common';
import { isLocationEqual } from '@skjv/common';
import { saveNotes, loadNotes } from '../persistence';

/**
 * Notes state management.
 */
export class NotesState {
  /** User notes */
  notes: Note[] = [];

  constructor() {
    makeAutoObservable(this);

    if (typeof window !== 'undefined') {
      this.notes = loadNotes<Note>();
    }
  }

  /** Get note at a specific location */
  getNoteAt(location: BibleLocation | null): Note | undefined {
    if (!location) return undefined;
    return this.notes.find((note) => isLocationEqual(note, location));
  }

  /** Save a note at a location */
  saveNote(location: BibleLocation, noteText: string): void {
    const existingIndex = this.notes.findIndex((n) => isLocationEqual(n, location));

    if (existingIndex >= 0) {
      this.notes = this.notes.map((n, i) => (i === existingIndex ? { ...n, noteText } : n));
    } else {
      this.notes = [...this.notes, { ...location, noteText }];
    }
    this.save();
  }

  /** Remove note at a location */
  removeNote(location: BibleLocation): void {
    this.notes = this.notes.filter((n) => !isLocationEqual(n, location));
    this.save();
  }

  /** Import notes (merges by location) */
  importNotes(notes: Note[]): number {
    let count = 0;
    for (const note of notes) {
      const exists = this.notes.some((n) => isLocationEqual(n, note));
      if (!exists) {
        this.notes.push(note);
        count++;
      }
    }
    this.save();
    return count;
  }

  private save(): void {
    saveNotes(this.notes);
  }
}
